// client/src/components/LanguageToggle.js
//
// THE LANGUAGE CHOICE, BEFORE SIGNING IN.
//
// The toggle existed on the landing page and in profile settings — one in front
// of the sign-in page and one behind it, with nothing on the page itself. So
// anybody who arrived at /login directly (which is what a bookmark, a shared
// link and the mobile app's own "open in browser" all do) had no way to change
// the language on the one screen where not understanding the words is what
// actually stops you.
//
// The mobile client has a counterpart at apps/mobile/src/components/LanguageToggle.js
// with the same behaviour and the same storage key, so the choice carries
// between the two.

import React from 'react';
import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

// The label a speaker of that language recognises. Never the English name of
// the language — somebody who cannot read the page cannot read "French" either,
// which is the whole point of putting the control here.
const LABEL = { en: 'EN', fr: 'FR' };
const FULL = { en: 'English', fr: 'Français' };

const LANGUAGES = ['en', 'fr'];

/**
 * @param {string} ink   the brand colour the selected pill fills with
 * @param {string} onInk the selected pill's own text colour
 */
export default function LanguageToggle({ ink = '#241C6B', onInk = '#FFFFFF', size = 'small', sx }) {
  const { i18n } = useTranslation();

  // `startsWith`, not equality: i18next resolves to regional tags — 'fr-CM' for
  // a browser set to Cameroonian French — and an equality test leaves neither
  // language showing as active.
  const isActive = (lng) => i18n.language === lng
    || String(i18n.language || '').startsWith(lng);

  const choose = (lng) => {
    // i18next's own detector caches the choice under the shared key, which is
    // why there is no explicit write here: changeLanguage triggers it.
    try { i18n.changeLanguage(lng); } catch (_) { /* a detector-less test render */ }
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={sx}>
      {LANGUAGES.map((lng) => {
        const active = isActive(lng);
        return (
          <Button
            key={lng}
            size={size}
            onClick={() => choose(lng)}
            aria-pressed={active}
            aria-label={FULL[lng] || lng}
            sx={{
              minWidth: 0,
              px: { xs: 0.9, sm: 1.2 },
              py: 0.35,
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 700,
              lineHeight: 1.4,
              borderRadius: 1,
              border: 1,
              borderColor: ink,
              color: active ? onInk : ink,
              bgcolor: active ? ink : 'transparent',
              '&:hover': { bgcolor: active ? ink : 'rgba(31, 95, 191, 0.08)' },
            }}
          >
            {LABEL[lng] || String(lng).toUpperCase()}
          </Button>
        );
      })}
    </Stack>
  );
}
